import React, { useCallback, useEffect, useState, useRef } from 'react';
import {
  Keyboard,
  Platform,
  Text,
  Pressable,
  StyleSheet,
  View,
  Image,
  TextInput,
  ViewStyle,
  Alert,
  TextStyle,
  ScrollView
} from 'react-native';
import { createPortal } from 'react-dom';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withSpring,
  withTiming,
  Easing,
  withSequence,
  withRepeat,
} from 'react-native-reanimated';
import Dropzone from 'react-dropzone';
import { fontValue } from './Dimensions';
import { COLORS } from './Misc';
import * as Animatable from 'react-native-animatable';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import DataSearch from './DataSearch';
import { AntDesign, FontAwesome, MaterialCommunityIcons } from '@expo/vector-icons';
import { Adapt, Avatar, Popover, Dialog, YStack, XStack, Button, TextArea, Switch } from 'tamagui';
import { Picker } from 'emoji-mart-native';
import { selectUser, selectUserId } from '@reduxLocal/selectors/userSelectors';
import { IMessage, InputToolbarProps } from 'react-native-gifted-chat';
import { MyInputToolbarProps } from '../types/MyInputToolbarProps';
import handleImage from "@utils/handleImage";
import { useDispatch, useSelector } from 'react-redux';
import { navigationRef } from '../../../../rootNavigation';
import { CHANGE_DATA, CHANGE_DATA_TAGS, SET_UPLOADER_STAGE, SHOW_LOADING_MODAL, HIDE_LOADING_MODAL, SHOW_AUTH_MODAL } from '@reduxLocal/actions/types';
import { uploadGlobalAsset } from '@api/api';
import Papa from 'papaparse';
import { Buffer } from 'buffer';
import apis from '@api/api';
import ParsedTextWithReferences from '@navigation/screens/MidLevel/EasyModeScreen/Components/ParsedTextWithReferences';
import { parseAllReferences, createDisplayTextWithPlaceholders } from '@navigation/screens/MidLevel/EasyModeScreen/utils/chatUtils';
import DataReferenceView from '@navigation/screens/MidLevel/EasyModeScreen/Components/DataReferenceView';
import ProductSelectionModal, { ProductSelectionModalHandle } from '@components/modals/ProductSelectionModal/ProductSelectionModal';
import { sendMediaMessage } from '@api/whatsappApi';

const MyInputToolbar = ({
  aiInputStyling = false,
  hasVoiceInput = false,
  messageGetter = undefined,
  personify = false,
  padTop = 0,
  targetUser = {},
  fromFeed = false,
  emptyPlaceHolder = "",
  fileDrop = false,
  isHelp = false,
  messageInput = false,
  messageSetter = (text) => { },
  forceColumn = false,
  sendMessageCallback = (message: any) => { },
  cameraPressCallback = () => { },
  attachPressCallback = () => { },
  userUID = "",
  removeTools = false,
  removeAttach = false,
  placeHolder = "",
  disableEntry = false,
  customSendIcon,
  maxHeight,
  multiline = false,
  inverse = false,
  removeBorderRadius = false,
  backgroundColor = 'white',
  staticPress = false,
  onSend,
  user,
  externalText,
  enableModelSelector = false,
  enableModeSelector = false,
  selectedMode = 'ask',
  onModeChange,
  onModelPress,
  onDataFileProcessed,
  draggedOver = false, // Default value for new prop
  onDragOver,
  onDragLeave,
  onDrop,
  onDragEnter,
  onMetadataSubmit, // Destructure new prop
  metadataDialogVisible,
  setMetadataDialogVisible,
  pendingMediaFile,
  setPendingMediaFile,
  mediaMetadata,
  setMediaMetadata,
  enableChatbotRouting = false,
  onChatbotRoutingChange,
  showChatbotSwitch = false,
  //Apply TextInputProps to the containerStyle
  containerStyle = {},
  textInputStyle = {},
}: MyInputToolbarProps & Partial<InputToolbarProps<IMessage>>) => {
  const height = useSharedValue(removeTools && !forceColumn ? 60 : (maxHeight || 140));
  const maxWidth = useSharedValue('100%');
  const opacity = useSharedValue(0);
  const [text, setText] = React.useState('');
  const [folder, showFolder] = React.useState(false);
  const [folderHiding, setFolderHiding] = React.useState(false);
  const theme = themeSelector();
  const [emojiState, setEmojiState] = useState(null);
  const [pendingImage, setPendingImage] = useState<{ uri?: string; base64?: string }>({});
  const [prevImage, setPrevImage] = useState<any>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const recordingTimer = useRef<NodeJS.Timeout | null>(null);
  const recognitionRef = useRef<any>(null);
  const micScale = useSharedValue(1);
  const dispatch = useDispatch();
  const currentUserId = useSelector(selectUserId);
  const containerRef = useRef<View>(null);
  const productSelectionRef = useRef<ProductSelectionModalHandle>(null);

  // Slash command state
  const [slashCommandVisible, setSlashCommandVisible] = useState(false);
  const [slashCommandQuery, setSlashCommandQuery] = useState('');
  const [slashCommandPosition, setSlashCommandPosition] = useState({ start: 0, end: 0 });
  const [referenceOptions, setReferenceOptions] = useState<{
    dataModels: any[];
    dataItems: any[];
    projects: any[];
    calendars: any[];
  }>({
    dataModels: [],
    dataItems: [],
    projects: [],
    calendars: []
  });
  const [loadingReferences, setLoadingReferences] = useState(false);
  const textInputRef = useRef<TextInput>(null);
  const [popupPosition, setPopupPosition] = useState<{ top: number; left: number; width: number } | null>(null);
  const blurTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isProcessingSlashCommandRef = useRef(false);
  const focusTimeRef = useRef<number>(0); // Track when input was focused
  const isTouchDeviceRef = useRef<boolean>(false); // Track if device is touch-enabled
  const shouldPreventBlurRef = useRef<boolean>(false); // Track if we should prevent blur
  const keyboardVisibleRef = useRef<boolean>(false); // Track if keyboard is visible
  const isBlurFromKeyboardRef = useRef<boolean>(false); // Track if blur is from keyboard appearing
  const dragTimeoutRef = useRef<NodeJS.Timeout | null>(null); // Timer to reset drag state after 3 seconds
  const [internalDraggedOver, setInternalDraggedOver] = useState(false); // Internal state to override draggedOver prop after timeout

  // Memoize parsed references for performance
  const parsedReferences = React.useMemo(() => {
    if (!text) return [];
    const segments = parseAllReferences(text);
    return segments.filter(seg => seg.type === 'reference' && seg.reference);
  }, [text]);

  // Create display text with references replaced by spaces (to hide them visually)
  const displayText = React.useMemo(() => {
    if (!text || parsedReferences.length === 0) return text;
    return createDisplayTextWithPlaceholders(text);
  }, [text, parsedReferences.length]);

  const heightAnimatedStyle = useAnimatedStyle(() => {
    return {
      height: withSpring(height?.value),
    };
  });

  const widthAnimatedStyle = useAnimatedStyle(() => {
    return {
      maxWidth: withTiming(maxWidth?.value as any, {
        duration: 250,
        easing: Easing.linear,
      }),
    };
  });

  const opacityAnimatedStyle = useAnimatedStyle(() => {
    return {
      opacity: withTiming(opacity?.value, {
        duration: 250,
        easing: Easing.circle,
      }),
    };
  });

  const handleEmojiOpen = (event) => {
    setEmojiState(event);
  }

  const [lastLength, setLastLength] = React.useState(0);
  const prevExternalText = React.useRef<string | undefined>(undefined);

  // Fetch reference data
  useEffect(() => {
    if (slashCommandVisible && !loadingReferences) {
      const fetchReferences = async () => {
        setLoadingReferences(true);
        try {
          const userId = userUID || currentUserId || '';
          const [dataFoldersResponse, projectsResponse, calendarsResponse] = await Promise.all([
            userId ? apis.getDataFolders({ userId, dataFilter: 'all' }).catch(() => []) : Promise.resolve([]),
            apis.getProjects().catch(() => []),
            apis.getCalendars().catch(() => [])
          ]);

          setReferenceOptions({
            dataModels: Array.isArray(dataFoldersResponse) ? dataFoldersResponse : (dataFoldersResponse?.data || []),
            dataItems: [],
            projects: Array.isArray(projectsResponse) ? projectsResponse : (projectsResponse?.data?.data || projectsResponse?.data || []),
            calendars: Array.isArray(calendarsResponse) ? calendarsResponse : (calendarsResponse?.data || [])
          });
        } catch (error) {
          console.error('Error fetching references:', error);
        } finally {
          setLoadingReferences(false);
        }
      };
      fetchReferences();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slashCommandVisible]);

  // Calculate popup position for web (Portal)
  useEffect(() => {
    if (Platform.OS !== 'web' || !slashCommandVisible || !textInputRef.current) return;

    const updatePosition = () => {
      try {
        // Try multiple ways to get the input element
        const inputElement = (textInputRef.current as any)?._nativeNode ||
          (textInputRef.current as any)?.nativeElement ||
          (typeof document !== 'undefined' ? document.activeElement : null);

        if (inputElement && typeof inputElement.getBoundingClientRect === 'function') {
          const rect = inputElement.getBoundingClientRect();
          setPopupPosition({
            top: Math.max(10, rect.top - 320), // Position above input, with min top margin
            left: Math.max(20, Math.min(rect.left, window.innerWidth - 420)), // Clamp to screen
            width: Math.min(400, Math.max(300, rect.width || 400))
          });
        } else if (containerRef.current) {
          // Fallback: use container position
          const containerElement = (containerRef.current as any)?._nativeNode ||
            (containerRef.current as any)?.nativeElement;
          if (containerElement && typeof containerElement.getBoundingClientRect === 'function') {
            const rect = containerElement.getBoundingClientRect();
            setPopupPosition({
              top: Math.max(10, rect.top - 320),
              left: Math.max(20, rect.left),
              width: Math.min(400, rect.width || 400)
            });
          }
        }
      } catch (e) {
        console.error('Error calculating popup position:', e);
      }
    };

    updatePosition();
    const scrollHandler = () => updatePosition();
    const resizeHandler = () => updatePosition();

    window.addEventListener('scroll', scrollHandler, true);
    window.addEventListener('resize', resizeHandler);

    return () => {
      window.removeEventListener('scroll', scrollHandler, true);
      window.removeEventListener('resize', resizeHandler);
    };
  }, [slashCommandVisible]);

  // Sync externalText to local state
  useEffect(() => {
    if (externalText !== undefined && externalText !== prevExternalText.current) {
      prevExternalText.current = externalText;
      if (externalText) {
        setText(externalText);
        messageSetter?.(externalText);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalText]);

  useEffect(() => {
    if (text?.trim()?.length > 0) {
      maxWidth.value = '85%';
      opacity.value = 1;
    } else {
      maxWidth.value = '100%';
      opacity.value = 0;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  const handleToggleFolder = () => {
    if (folder) {
      setFolderHiding(true)
      setTimeout(() => {
        showFolder(false)
      }, 200);
    } else {
      showFolder(true)
      setTimeout(() => setFolderHiding(false), 200)
    }
  }

  // Voice input functions
  const startVoiceRecording = async () => {
    try {
      // Check if Web Speech API is available (for web)
      if (Platform.OS === 'web' && 'webkitSpeechRecognition' in window) {
        const SpeechRecognition = (window as any).webkitSpeechRecognition || (window as any).SpeechRecognition;
        const recognition = new SpeechRecognition();

        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = 'en-US';

        recognition.onstart = () => {
          setIsRecording(true);
          micScale.value = withRepeat(
            withSequence(
              withTiming(1.3, { duration: 500 }),
              withTiming(1, { duration: 500 })
            ),
            -1,
            true
          );
          // Start timer
          recordingTimer.current = setInterval(() => {
            setRecordingTime((prev) => prev + 1);
          }, 1000);
        };

        recognition.onresult = (event: any) => {
          let finalTranscript = '';
          let interimTranscript = '';

          for (let i = event.resultIndex; i < event.results.length; i++) {
            const transcript = event.results[i][0].transcript;
            if (event.results[i].isFinal) {
              finalTranscript += transcript + ' ';
            } else {
              interimTranscript += transcript;
            }
          }

          if (finalTranscript) {
            const newText = text + finalTranscript;
            setText(newText);
            messageSetter?.(newText);
          }
        };

        recognition.onerror = (event: any) => {
          console.error('Speech recognition error:', event.error);
          stopVoiceRecording();
          Alert.alert('Voice Input Error', `Could not process voice: ${event.error}`);
        };

        recognition.onend = () => {
          stopVoiceRecording();
        };

        recognition.start();
        recognitionRef.current = recognition;
      } else {
        // For mobile - show alert that feature is available on web
        Alert.alert(
          'Voice Input',
          'Voice input is currently available on web browsers. Mobile voice input coming soon!',
          [
            {
              text: 'OK',
              style: 'default'
            }
          ]
        );
      }
    } catch (error) {
      console.error('Error starting voice recording:', error);
      Alert.alert('Error', 'Failed to start voice recording. Please check microphone permissions.');
    }
  };

  const stopVoiceRecording = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }

    if (recordingTimer.current) {
      clearInterval(recordingTimer.current);
      recordingTimer.current = null;
    }

    setIsRecording(false);
    setRecordingTime(0);
    micScale.value = withTiming(1, { duration: 200 });
  };

  const toggleVoiceRecording = () => {
    if (isRecording) {
      stopVoiceRecording();
    } else {
      startVoiceRecording();
    }
  };

  // Detect touch device early (on mount)
  useEffect(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      isTouchDeviceRef.current = 'ontouchstart' in window ||
        navigator.maxTouchPoints > 0 ||
        (window as any).DocumentTouch !== undefined;
    }
  }, []);

  // Listen for keyboard events to track keyboard visibility
  useEffect(() => {
    if (Platform.OS === 'web') return; // Keyboard events are native-only

    const showSubscription = Keyboard.addListener('keyboardDidShow', () => {
      keyboardVisibleRef.current = true;
      // If keyboard just appeared and we have a blur, it's likely from keyboard appearance
      if (isBlurFromKeyboardRef.current) {
        // Restore focus after keyboard animation completes
        setTimeout(() => {
          textInputRef.current?.focus();
          isBlurFromKeyboardRef.current = false;
        }, 100);
      }
    });

    const hideSubscription = Keyboard.addListener('keyboardDidHide', () => {
      keyboardVisibleRef.current = false;
      isBlurFromKeyboardRef.current = false;
    });

    return () => {
      showSubscription.remove();
      hideSubscription.remove();
    };
  }, []);

  // Handle drag timeout - reset drag state after 3 seconds if no drop occurs
  useEffect(() => {
    // Clear any existing timeout
    if (dragTimeoutRef.current) {
      clearTimeout(dragTimeoutRef.current);
      dragTimeoutRef.current = null;
    }

    if (draggedOver && Platform.OS === 'web') {
      // Set internal state to true when drag starts
      setInternalDraggedOver(true);

      // Set timer to reset after 3 seconds
      dragTimeoutRef.current = setTimeout(() => {
        setInternalDraggedOver(false);
        dragTimeoutRef.current = null;
      }, 3000);
    } else {
      // Reset internal state when draggedOver becomes false
      setInternalDraggedOver(false);
    }

    // Cleanup on unmount or when draggedOver changes
    return () => {
      if (dragTimeoutRef.current) {
        clearTimeout(dragTimeoutRef.current);
        dragTimeoutRef.current = null;
      }
    };
  }, [draggedOver]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopVoiceRecording();
      // Clear blur timeout on unmount
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
        blurTimeoutRef.current = null;
      }
      // Clear drag timeout on unmount
      if (dragTimeoutRef.current) {
        clearTimeout(dragTimeoutRef.current);
        dragTimeoutRef.current = null;
      }
    };
  }, []);

  // Filter reference options based on query
  const getFilteredReferences = () => {
    const query = slashCommandQuery.toLowerCase().trim();
    const results: Array<{
      type: 'dataModel' | 'dataItem' | 'project' | 'calendar';
      id: string;
      title: string;
      subtitle?: string;
      data: any;
    }> = [];

    // Filter data models
    referenceOptions.dataModels.forEach((model: any) => {
      const title = model.dataTitle || model.title || '';
      if (!query || new RegExp(query, 'i').test(title)) {
        results.push({
          type: 'dataModel',
          id: model._id,
          title,
          subtitle: model.dataTags?.join(', ') || '',
          data: model
        });
      }
    });

    // Filter data items
    referenceOptions.dataItems.forEach((item: any) => {
      const title = item.dataTitle || item.title || Object.values(item.data || {})[0] || 'Untitled Item';
      if (!query || new RegExp(query, 'i').test(title)) {
        results.push({
          type: 'dataItem',
          id: item._id,
          title: String(title),
          subtitle: `From ${item.dataTitle || 'Data Model'}`,
          data: item
        });
      }
    });

    // Filter projects
    referenceOptions.projects.forEach((project: any) => {
      const title = project.title || '';
      if (!query || new RegExp(query, 'i').test(title)) {
        results.push({
          type: 'project',
          id: project._id,
          title,
          subtitle: project.description || '',
          data: project
        });
      }
    });

    // Filter calendars
    referenceOptions.calendars.forEach((calendar: any) => {
      const title = calendar.title || calendar.name || '';
      if (!query || new RegExp(query, 'i').test(title)) {
        results.push({
          type: 'calendar',
          id: calendar._id || calendar.calendarId,
          title,
          subtitle: calendar.description || '',
          data: calendar
        });
      }
    });

    return results.slice(0, 10); // Limit to 10 results
  };

  // Insert reference into text
  const insertReference = React.useCallback((reference: { type: string; id: string; title: string }) => {
    isProcessingSlashCommandRef.current = true;

    const beforeSlash = text.substring(0, slashCommandPosition.start);
    const afterCursor = text.substring(slashCommandPosition.end);

    let referenceText = '';
    switch (reference.type) {
      case 'dataModel':
        referenceText = `[@data:${reference.id} "${reference.title}"]`;
        break;
      case 'dataItem':
        referenceText = `[@item:${reference.id} "${reference.title}"]`;
        break;
      case 'project':
        referenceText = `[@project:${reference.id} "${reference.title}"]`;
        break;
      case 'calendar':
        referenceText = `[@calendar:${reference.id} "${reference.title}"]`;
        break;
    }

    const newText = beforeSlash + referenceText + afterCursor;
    setText(newText);
    messageSetter?.(newText);
    setSlashCommandVisible(false);
    setSlashCommandQuery('');

    // Clear any pending blur timeout
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }

    // Focus back on input and position cursor
    requestAnimationFrame(() => {
      setTimeout(() => {
        textInputRef.current?.focus();
        isProcessingSlashCommandRef.current = false;
        const newCursorPosition = beforeSlash.length + referenceText.length;
        // Note: TextInput selection positioning requires native ref handling
      }, 100);
    });
  }, [text, slashCommandPosition, messageSetter]);

  // On touch devices, completely bypass Dropzone to prevent document-level listeners
  const isTouchDevice = isTouchDeviceRef.current;
  const shouldUseDropzone = fileDrop && Platform.OS === 'web' && !isTouchDevice;

  // Use internal draggedOver state if available, otherwise use prop
  // This allows us to override the prop after 3 seconds
  const effectiveDraggedOver = internalDraggedOver && draggedOver;

  // Enhanced drag handlers that clear timeout on drop or leave
  const handleDragOver = useCallback((e: any) => {
    // Don't reset timer on every dragOver event - it fires continuously
    // The timer is managed by the useEffect that watches the draggedOver prop
    // Call original handler if provided
    onDragOver?.(e);
  }, [onDragOver]);

  const handleDragLeave = useCallback((e: any) => {
    // Clear timeout when drag leaves
    if (dragTimeoutRef.current) {
      clearTimeout(dragTimeoutRef.current);
      dragTimeoutRef.current = null;
    }
    setInternalDraggedOver(false);

    // Call original handler if provided
    onDragLeave?.(e);
  }, [onDragLeave]);

  const handleDrop = useCallback((e: any) => {
    // Clear timeout when file is dropped
    if (dragTimeoutRef.current) {
      clearTimeout(dragTimeoutRef.current);
      dragTimeoutRef.current = null;
    }
    setInternalDraggedOver(false);

    // Call original handler if provided
    onDrop?.(e);
  }, [onDrop]);

  return (
    <Dropzone
      onDrop={shouldUseDropzone ? handleDrop : undefined}
      onDragOver={shouldUseDropzone ? handleDragOver : undefined}
      onDragLeave={shouldUseDropzone ? handleDragLeave : undefined}
      noClick
      noKeyboard
      disabled={!shouldUseDropzone}
      // Prevent Dropzone from interfering with touch events on web
      preventDropOnDocument={Platform.OS === 'web'}
    >
      {({ getRootProps, getInputProps }) => {
        const rootProps = getRootProps();
        // Filter out web-specific props that don't work with React Native View
        // Keep touch events for web to prevent interference with TextInput
        const { onBlur, onFocus, tabIndex, onMouseEnter, onMouseLeave, ...restRootProps } = rootProps as any;
        return (
          <View
            {...restRootProps} // Spread the rest of the props
            ref={containerRef}
            style={[
              {
                top: multiline ? 22 : undefined,
                overflow: "visible",
                maxHeight: 250,
                flex: 1,
                minHeight: aiInputStyling && 220,
                borderWidth: effectiveDraggedOver && Platform.OS === 'web' ? 3 : 4,
                borderColor: effectiveDraggedOver && Platform.OS === 'web' ? '#3b82f6' : 'transparent',
                borderStyle: effectiveDraggedOver && Platform.OS === 'web' ? 'dashed' : 'solid',
                justifyContent: aiInputStyling ? undefined : "center",
                borderBottomStartRadius: 15,
                borderBottomEndRadius: 15,
                borderTopStartRadius: removeBorderRadius ? 0 : 15,
                borderTopEndRadius: removeBorderRadius ? 0 : 15,
              },
              containerStyle
            ]}
            // Prevent touch events from bubbling to document (which might cause focus loss)
            onTouchStart={(e) => {
              if (Platform.OS === 'web' && isTouchDevice) {
                e.stopPropagation();
                shouldPreventBlurRef.current = true;
                // Reset after a short delay
                setTimeout(() => {
                  shouldPreventBlurRef.current = false;
                }, 100);
              }
            }}
            onTouchEnd={(e) => {
              if (Platform.OS === 'web' && isTouchDevice) {
                e.stopPropagation();
              }
            }}
            onTouchMove={(e) => {
              if (Platform.OS === 'web' && isTouchDevice) {
                e.stopPropagation();
              }
            }}
          >
            {shouldUseDropzone && <input {...getInputProps()} />}
            {/* Drag Overlay (Web Only) */}
            {effectiveDraggedOver && Platform.OS === 'web' && (
              <View style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(59, 130, 246, 0.1)',
                borderRadius: 15,
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 1000,
                borderWidth: 3,
                borderColor: '#3b82f6',
                borderStyle: 'dashed',
              }}>
                <View style={{
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <AntDesign name="arrow-up" size={48} color="#3b82f6" style={{ marginBottom: 8 }} />
                  <MaterialCommunityIcons name="file-document-outline" size={32} color="#3b82f6" style={{ marginBottom: 12 }} />
                  <Text style={{
                    color: '#3b82f6',
                    fontSize: 16,
                    fontWeight: '600',
                    marginTop: 4
                  }}>
                    Drop files here
                  </Text>
                  <Text style={{
                    color: theme ? '#666' : '#999',
                    fontSize: 12,
                    marginTop: 4
                  }}>
                    CSV, Excel, Images, or Videos
                  </Text>
                </View>
              </View>
            )}

            {
              !removeTools &&
              <Animatable.View transition={'top'} duration={100} direction={'alternate'} style={{ top: !folderHiding ? -80 : 0 }}>
                {
                  folder &&
                  <Animatable.View useNativeDriver animation={'fadeIn'} style={{ flexDirection: 'row', position: "absolute", alignSelf: 'stretch', minHeight: 70 }}>
                    <DataSearch callback={(value) => console.log(value)} />
                  </Animatable.View>
                }
              </Animatable.View>
            }
            <Animated.View
              style={[
                styles.container,
                heightAnimatedStyle /*messageInputTranslate*/,
              ]}>
              {(enableModelSelector || enableModeSelector) && (
                <View style={{
                  position: 'absolute',
                  top: Platform.select({ native: -50, web: -70 }),
                  left: 50,
                  right: 0,
                  zIndex: 100,
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  paddingHorizontal: 16,
                  alignItems: 'center',
                  width: '95%'
                }}>
                  {enableModelSelector && (
                    <Pressable
                      onPress={onModelPress}
                      style={({ pressed }) => ({
                        flexDirection: 'row',
                        alignItems: 'center',
                        backgroundColor: theme ? '#f0f0f0' : '#333',
                        paddingHorizontal: 12,
                        paddingVertical: 6,
                        borderRadius: 12,
                        opacity: pressed ? 0.7 : 1,
                      })}
                    >
                      <Text style={{ color: theme ? '#000' : '#fff', fontSize: 13, fontWeight: '600' }}>SmartSaaS 1.0</Text>
                      <AntDesign name="down" size={12} color={theme ? '#666' : '#aaa'} style={{ marginLeft: 6 }} />
                    </Pressable>
                  )}

                  {enableModeSelector && (
                    <View style={{ flexDirection: 'row', backgroundColor: theme ? '#f0f0f0' : '#333', borderRadius: 12, padding: 3, marginLeft: 'auto' }}>
                      {['Ask', 'Plan', 'Magic'].map((mode) => {
                        const isActive = selectedMode?.toLowerCase() === mode.toLowerCase();
                        return (
                          <Pressable
                            key={mode}
                            onPress={() => onModeChange?.(mode.toLowerCase())}
                            style={{
                              paddingHorizontal: 12,
                              paddingVertical: 5,
                              borderRadius: 10,
                              backgroundColor: isActive ? (theme ? '#fff' : '#555') : 'transparent',
                              shadowColor: "#000",
                              shadowOffset: { width: 0, height: 1 },
                              shadowOpacity: isActive ? 0.1 : 0,
                              shadowRadius: 1,
                            }}
                          >
                            <Text style={{ fontSize: 13, fontWeight: isActive ? '600' : '400', color: theme ? '#000' : '#fff' }}>{mode}</Text>
                          </Pressable>
                        )
                      })}
                    </View>
                  )}
                </View>
              )}
              <View style={[styles.innerContainer, { alignSelf: "stretch", flexDirection: forceColumn ? (inverse ? "column" : "column-reverse") : "row", maxHeight: 40, overflow: Platform.OS !== 'web' ? 'visible' : undefined }]}>
                <Animated.View
                  style={[styles.inputAndMicrophone, widthAnimatedStyle, { borderRadius: removeBorderRadius && 0, overflow: "visible", minWidth: "100%", paddingHorizontal: 10, backgroundColor: "transparent", zIndex: Platform.OS !== 'web' ? 1 : undefined }]}>
                  <View style={{ overflow: "visible", gap: 5, flexDirection: forceColumn ? (inverse ? "column" : "column-reverse") : "row-reverse", minWidth: "105%", left: -20, flex: 1, position: 'relative' as any }}>
                    {
                      !removeTools &&
                      <View style={{ paddingHorizontal: 20, flexDirection: "column", gap: 15, justifyContent: "space-between" }}>
                        <View style={{ flex: 1, flexDirection: "row", gap: 5 }}>
                          <Popover
                            allowFlip
                            stayInFrame
                            offset={{ mainAxis: -10, crossAxis: -10 }}
                          >
                            <Popover.Content
                              borderWidth={0}
                              unstyled
                              enterStyle={{ y: 10, opacity: 0 }}
                              exitStyle={{ y: 10, opacity: 0 }}
                              elevate
                              style={{ borderRadius: 20, borderWidth: 0, margin: -10 }}
                              animation={[
                                'quick',
                                {
                                  opacity: {
                                    overshootClamping: true,
                                  },
                                },
                              ]}
                            >
                              <Picker
                                onSelect={(emoji) => { let coll = text + emoji.native; setText(coll); messageSetter?.(coll); setLastLength(coll.length) }}
                              />
                            </Popover.Content>
                            <Popover.Trigger>
                              <FontAwesome
                                name={emojiState ? 'close' : 'smile-o'}
                                size={forceColumn ? 18 : 23}
                                color={theme ? COLORS.lightGrey : 'black'}
                                style={{ top: !multiline && 5 }}
                              />
                            </Popover.Trigger>
                          </Popover>

                          <>
                            {
                              !removeAttach &&
                              <Pressable
                                android_ripple={{
                                  color: COLORS.controlHighlight,
                                  borderless: true,
                                  radius: 30 - 0.1 * 30,
                                }}
                                style={styles.rightIconButtonStyle}
                                onPress={attachPressCallback}>
                                <FontAwesome
                                  name="paperclip"
                                  size={forceColumn ? 18 : 23}
                                  color={theme ? COLORS.darkGrey : 'black'}
                                />
                              </Pressable>
                            }
                          </>
                          <Pressable
                            hitSlop={10}
                            android_ripple={{
                              color: COLORS.controlHighlight,
                              borderless: true,
                              radius: 30 - 0.1 * 30,
                            }}
                            style={styles.rightIconButtonStyle}
                            onPress={async () => {
                              await handleImage(true).then(res => {
                                if (typeof res === 'string') {
                                  setPendingImage({ uri: res });
                                } else if (res && typeof res === 'object') {
                                  setPendingImage({ uri: res.uri, base64: res.base64 });
                                }
                              })
                              cameraPressCallback?.()
                            }}>
                            <FontAwesome
                              name="image"
                              size={forceColumn ? 18 : 23}
                              color={theme ? COLORS.darkGrey : 'black'}
                            />
                          </Pressable>
                          {showChatbotSwitch && (
                              <>
                                <Pressable
                                  onPress={() => productSelectionRef.current?.openModal()}
                                  style={{
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    gap: 6,
                                    paddingHorizontal: 8,
                                    paddingVertical: 4,
                                    borderRadius: 12,
                                    backgroundColor: theme ? '#f0f9ff' : '#0c4a6e',
                                    borderWidth: 1,
                                    borderColor: theme ? '#bae6fd' : '#075985'
                                  }}
                                >
                                  <MaterialCommunityIcons
                                    name="shopping"
                                    size={forceColumn ? 16 : 18}
                                    color={theme ? '#0ea5e9' : '#38bdf8'}
                                  />
                                  <Text style={{
                                    color: theme ? '#0ea5e9' : '#38bdf8',
                                    fontSize: forceColumn ? 11 : 13,
                                    fontWeight: '600'
                                  }}>
                                    Product
                                  </Text>
                                </Pressable>
                                <View style={{
                                  flexDirection: 'row',
                                  alignItems: 'center',
                                  gap: 6,
                                  paddingHorizontal: 8,
                                  paddingVertical: 4,
                                  borderRadius: 12,
                                  backgroundColor: enableChatbotRouting ? 'rgba(37, 211, 102, 0.1)' : 'transparent'
                                }}>
                                  <MaterialCommunityIcons
                                    name="robot"
                                    size={forceColumn ? 16 : 18}
                                    color={enableChatbotRouting ? '#25D366' : (theme ? COLORS.darkGrey : '#999')}
                                  />
                                  <Switch
                                    checked={enableChatbotRouting}
                                    onCheckedChange={onChatbotRoutingChange}
                                    size="$2"
                                    backgroundColor={enableChatbotRouting ? '#25D366' : (theme ? '#ccc' : '#666')}
                                  >
                                    <Switch.Thumb backgroundColor={theme ? "white" : "#f0f0f0"} />
                                  </Switch>
                                </View>
                              </>
                          )}
                        </View>
                      </View>
                    }
                    <View style={{ flexDirection: "row", flex: 1.5, paddingLeft: 25, paddingTop: enableModelSelector && 90, position: 'relative' }}>
                      {
                        pendingImage?.uri &&
                        <>
                          <Pressable onPress={(e) => setPrevImage(e.currentTarget)}>
                            <Animatable.Image animation={'slideInLeft'} style={{ width: 50, height: 50, resizeMode: 'contain' }} source={{ uri: pendingImage?.uri }} />
                          </Pressable>
                          <Popover
                            open={!!prevImage}
                            onOpenChange={(open) => !open && setPrevImage(null)}
                          >
                            <Pressable onPress={() => setPendingImage({})} style={{ flexDirection: "row" }}>
                              <Text>Remove</Text>
                              <AntDesign name="close" size={24} color="red" />
                            </Pressable>
                            <Image source={{ uri: pendingImage?.uri }} style={{ width: 200, height: 200, resizeMode: 'contain' }} />
                          </Popover>
                        </>
                      }
                      {
                        personify &&
                        <Avatar circular size={40} style={{ top: 4, left: -5 }}>
                          <Avatar.Image alt={`userAvatar`} src={user?.avatar || "https://storage.googleapis.com/smartsaas-avatar-store/Asset%202.png"} />
                          <Avatar.Fallback />
                        </Avatar>
                      }

                      {/* OPTION A FIX: stop Dropzone root from receiving the same click/touch that focuses the TextInput */}
                      <View
                        style={{ flex: 1, position: 'relative' }}
                        {...(Platform.OS === 'web'
                          ? ({
                            // RNW supports these; cast to any to avoid TS type complaints depending on RN versions
                            onMouseDownCapture: (e: any) => {
                              e?.stopPropagation?.();
                            },
                            onTouchStartCapture: (e: any) => {
                              e?.stopPropagation?.();
                            },
                            onPointerDownCapture: (e: any) => {
                              e?.stopPropagation?.();
                            },
                          } as any)
                          : {})}
                      >
                        <TextInput
                          ref={textInputRef}
                          multiline
                          numberOfLines={3}
                          editable={!disableEntry}
                          scrollEnabled={false}
                          placeholderTextColor={theme ? "black" : "white"}
                          placeholder={placeHolder ? (disableEntry ? "Awaiting response" : placeHolder) : (disableEntry ? "Awaiting response" : 'Aa')}
                          // Disable auto-correct and auto-capitalize to reduce re-renders on web
                          autoCorrect={Platform.OS !== 'web'}
                          autoCapitalize="none"
                          // Prevent text input from losing focus on web touch devices
                          blurOnSubmit={false}
                          onBlur={() => {
                            const timeSinceFocus = Date.now() - focusTimeRef.current;
                            console.log('⚠️ [InputToolbar] BLUR EVENT FIRED - time since focus:', timeSinceFocus, 'ms');
                            console.log('⚠️ [InputToolbar] Platform:', Platform.OS);
                            console.log('⚠️ [InputToolbar] keyboardVisibleRef.current:', keyboardVisibleRef.current);

                            // On web, prevent blur if it happens too quickly after focus
                            // This prevents the keyboard from dismissing when it first appears
                            if (Platform.OS === 'web') {
                              // If we just had a touch event, prevent blur
                              if (shouldPreventBlurRef.current) {
                                console.log('🔄 [InputToolbar] Web touch blur prevented - restoring focus');
                                // Immediate focus restoration
                                textInputRef.current?.focus();
                                setTimeout(() => {
                                  textInputRef.current?.focus();
                                }, 0);
                                setTimeout(() => {
                                  textInputRef.current?.focus();
                                }, 10);
                                return; // Don't process this blur event
                              }
                              // If blur happens within 1000ms of focus, it's likely a false blur from keyboard appearing
                              if (timeSinceFocus < 1000) {
                                console.log('🔄 [InputToolbar] Web keyboard blur detected - restoring focus immediately');
                                // IMMEDIATE focus restoration - multiple attempts
                                if (textInputRef.current) {
                                  try {
                                    textInputRef.current.focus();
                                    console.log('✅ [InputToolbar] Web immediate focus attempt');
                                  } catch (e) {
                                    console.warn('⚠️ [InputToolbar] Web immediate focus failed:', e);
                                  }
                                }
                                setTimeout(() => {
                                  textInputRef.current?.focus();
                                  console.log('✅ [InputToolbar] Web 0ms focus attempt');
                                }, 0);
                                setTimeout(() => {
                                  textInputRef.current?.focus();
                                  console.log('✅ [InputToolbar] Web 10ms focus attempt');
                                }, 10);
                                setTimeout(() => {
                                  textInputRef.current?.focus();
                                  console.log('✅ [InputToolbar] Web 50ms focus attempt');
                                }, 50);
                                return; // Don't process this blur event
                              }
                            }

                            // On native platforms, check if blur is from keyboard appearing
                            if (Platform.OS !== 'web') {
                              // If blur happens within 1000ms of focus, it's likely from keyboard appearing or ScrollView scrolling
                              // Increased window to catch ScrollView-triggered blurs that happen after keyboard animation
                              if (timeSinceFocus < 1000) {
                                console.log('🔄 [InputToolbar] Native keyboard blur detected - restoring focus immediately');
                                isBlurFromKeyboardRef.current = true;
                                // Don't dismiss keyboard - let it stay open
                                // Restore focus IMMEDIATELY - use multiple attempts to ensure it works
                                // First attempt: immediate (synchronous if possible)
                                if (textInputRef.current) {
                                  try {
                                    textInputRef.current.focus();
                                    console.log('✅ [InputToolbar] Immediate focus attempt');
                                  } catch (e) {
                                    console.warn('⚠️ [InputToolbar] Immediate focus failed:', e);
                                  }
                                }
                                // Second attempt: after current event loop (very fast)
                                setTimeout(() => {
                                  textInputRef.current?.focus();
                                  console.log('✅ [InputToolbar] 0ms focus attempt');
                                }, 0);
                                // Third attempt: after layout (slightly delayed for safety)
                                requestAnimationFrame(() => {
                                  setTimeout(() => {
                                    textInputRef.current?.focus();
                                    console.log('✅ [InputToolbar] 10ms focus attempt');
                                  }, 10); // Very short delay - just enough for layout
                                });
                                // Fourth attempt: slightly longer delay as backup
                                setTimeout(() => {
                                  textInputRef.current?.focus();
                                  console.log('✅ [InputToolbar] 50ms focus attempt (backup)');
                                }, 50);
                                // Still handle menu hiding logic
                                if (blurTimeoutRef.current) {
                                  clearTimeout(blurTimeoutRef.current);
                                }
                                blurTimeoutRef.current = setTimeout(() => {
                                  if (slashCommandVisible && !isProcessingSlashCommandRef.current) {
                                    setSlashCommandVisible(false);
                                  }
                                  blurTimeoutRef.current = null;
                                }, 300);
                                return; // Don't process this blur event further - don't call Keyboard.dismiss()
                              }

                              // Only dismiss keyboard if it's a legitimate blur (user tapped away)
                              // Add a delay to ensure this isn't a false blur from layout shift
                              setTimeout(() => {
                                // Check if keyboard is still visible before dismissing
                                // If keyboard just appeared, don't dismiss it
                                if (!isBlurFromKeyboardRef.current && keyboardVisibleRef.current) {
                              // This is a legitimate blur - user tapped away
                                  Keyboard.dismiss();
                                }
                              }, 200);
                            }
                            // Delay hiding menu to allow for clicks on the popup
                            // Only hide if popup is not being interacted with
                            if (blurTimeoutRef.current) {
                              clearTimeout(blurTimeoutRef.current);
                            }
                            blurTimeoutRef.current = setTimeout(() => {
                              // Check if popup is still visible before hiding
                              // This prevents hiding if user clicked on popup item
                              if (slashCommandVisible && !isProcessingSlashCommandRef.current) {
                                setSlashCommandVisible(false);
                              }
                              blurTimeoutRef.current = null;
                            }, 300);
                          }}
                          onFocus={() => {
                            // Track focus time for all platforms
                            focusTimeRef.current = Date.now();
                            isBlurFromKeyboardRef.current = false;
                            console.log('✅ [InputToolbar] Focused at', focusTimeRef.current);

                            // Track focus time for web touch devices
                            if (Platform.OS === 'web') {
                              // Detect touch device on first focus
                              if (!isTouchDeviceRef.current) {
                                isTouchDeviceRef.current = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
                              }
                            }

                            if (emojiState) {
                              setEmojiState(false);
                            }
                          }}
                          style={[styles.input, { left: !personify ? -20 : undefined, fontSize: 14, borderColor: "#00000030", minHeight: multiline ? 200 : undefined, minWidth: removeTools ? "90%" : undefined }, textInputStyle, (enableModelSelector || enableModeSelector) && { paddingTop: 20 }]}
                          value={displayText}
                          onKeyPress={(e) => {
                            //Detect cmd + enter
                            if (e.nativeEvent.key === 'Enter' && (e.nativeEvent as any).metaKey) {
                              if (text?.trim()?.length > 0 || pendingImage?.base64) {
                                if (onSend) {
                                  onSend?.({
                                    text: text,
                                    image: pendingImage.uri || pendingImage.base64 || '',
                                    createdAt: new Date(),
                                    user: {
                                      _id: user._id,
                                      avatar: user.avatar,
                                    },
                                    to: targetUser?.id,
                                  } as any);
                                } else {
                                  sendMessageCallback?.(text);
                                  //Lose focus on the input
                                  Keyboard.dismiss();
                                }
                                setText('');
                                //Lose focus
                              }
                            }
                          }}
                          onChangeText={React.useCallback((newText: string) => {
                            // newText is the display text (with spaces for references)
                            // We need to restore the actual references when mapping back
                            let actualText = newText;

                            // If we have references, restore them by mapping spaces back to reference patterns
                            if (parsedReferences.length > 0 && text) {
                              const segments = parseAllReferences(text);
                              let restoredText = '';
                              let displayIndex = 0;

                              segments.forEach((segment) => {
                                if (segment.type === 'reference' && segment.reference) {
                                  const refLength = segment.content.length;
                                  const displaySegment = newText.substring(displayIndex, displayIndex + refLength);

                                  // If it's all spaces, restore the reference
                                  if (displaySegment.trim().length === 0 || displaySegment === ' '.repeat(refLength)) {
                                    restoredText += segment.content;
                                  } else {
                                    // User edited this area, keep their changes
                                    restoredText += displaySegment;
                                  }
                                  displayIndex += refLength;
                                } else {
                                  // Regular text - take from display
                                  const segmentLength = segment.content.length;
                                  restoredText += newText.substring(displayIndex, displayIndex + segmentLength);
                                  displayIndex += segmentLength;
                                }
                              });

                              // Add any remaining text
                              if (displayIndex < newText.length) {
                                restoredText += newText.substring(displayIndex);
                              }

                              actualText = restoredText;
                            }

                            setText(actualText);
                            messageSetter?.(actualText);
                            setLastLength(actualText?.length);

                            requestAnimationFrame(() => {
                              // Detect slash command
                              const cursorPosition = newText.length; // Approximate cursor position
                              const textBeforeCursor = newText.substring(0, cursorPosition);
                              const lastSlashIndex = textBeforeCursor.lastIndexOf('/');

                              // Check if there's a slash and no space after it (indicating command mode)
                              if (lastSlashIndex !== -1) {
                                const textAfterSlash = textBeforeCursor.substring(lastSlashIndex + 1);
                                const hasWhitespaceAfter = /\s/.test(textAfterSlash);

                                if (!hasWhitespaceAfter && textAfterSlash.length >= 0) {
                                  console.log('🔍 [InputToolbar] Showing slash command menu', { textAfterSlash, lastSlashIndex });
                                  isProcessingSlashCommandRef.current = true;
                                  setSlashCommandVisible(true);
                                  setSlashCommandQuery(textAfterSlash);
                                  setSlashCommandPosition({
                                    start: lastSlashIndex,
                                    end: cursorPosition
                                  });
                                  if (Platform.OS === 'web') {
                                    setPopupPosition(null);
                                  }
                                  setTimeout(() => {
                                    isProcessingSlashCommandRef.current = false;
                                  }, 100);
                                } else {
                                  console.log('🔍 [InputToolbar] Hiding slash command menu - whitespace detected');
                                  isProcessingSlashCommandRef.current = false;
                                  setSlashCommandVisible(false);
                                  setSlashCommandQuery('');
                                  setPopupPosition(null);
                                }
                              } else {
                                isProcessingSlashCommandRef.current = false;
                                setSlashCommandVisible(false);
                                setSlashCommandQuery('');
                                setPopupPosition(null);
                              }
                            });
                          }, [messageSetter])}
                        />

                        {/* Parsed References - Positioned inline within input, replacing the text */}
                        {parsedReferences.length > 0 && (
                          <View
                            style={{
                              position: 'absolute',
                              top: (enableModelSelector || enableModeSelector) ? 20 : 12,
                              left: (!personify ? -20 : 0) + 12,
                              right: 50, // Leave space for send button
                              pointerEvents: 'none',
                              zIndex: 10,
                            }}
                            pointerEvents="box-none"
                          >
                            {(() => {
                              const segments = parseAllReferences(text);
                              let cumulativeWidth = 0;
                              const positionedRefs: Array<{ ref: typeof parsedReferences[0]; left: number; width: number }> = [];

                              segments.forEach((segment) => {
                                if (segment.type === 'reference' && segment.reference) {
                                  const refWidth = segment.content.length * 8.4; // Space width approximation
                                  positionedRefs.push({
                                    ref: segment,
                                    left: cumulativeWidth,
                                    width: refWidth
                                  });
                                  cumulativeWidth += refWidth;
                                } else {
                                  cumulativeWidth += segment.content.length * 8.4;
                                }
                              });

                              return positionedRefs.map(({ ref, left, width }, index) => {
                                if (!ref.reference) return null;
                                return (
                                  <View
                                    key={`input-ref-${ref.reference.id}-${index}`}
                                    style={{
                                      position: 'absolute',
                                      left: left,
                                      top: 0,
                                      backgroundColor: theme ? '#f5f5f5' : '#2a2a2a',
                                      borderRadius: 6,
                                      paddingHorizontal: 2,
                                      paddingVertical: 1,
                                    }}
                                  >
                                    <DataReferenceView
                                      type={ref.reference.type}
                                      title={ref.reference.title}
                                      id={ref.reference.id}
                                      theme={theme}
                                    />
                                  </View>
                                );
                              });
                            })()}
                          </View>
                        )}
                      </View>
                    </View>
                  </View>

                  {/* Reference Menu (Slash Command) */}
                  {slashCommandVisible && (() => {
                    const baseStyle = {
                      backgroundColor: theme ? '#ffffff' : '#1e293b',
                      borderRadius: 12,
                      maxHeight: 300,
                      shadowColor: '#000',
                      shadowOffset: { width: 0, height: 4 },
                      shadowOpacity: 0.3,
                      shadowRadius: 8,
                      elevation: 8,
                      borderWidth: 1,
                      borderColor: theme ? '#e2e8f0' : '#334155',
                      zIndex: 10000,
                      overflow: 'hidden' as const
                    };

                    const webStyle = Platform.OS === 'web' ? {
                      ...baseStyle,
                      position: 'fixed' as any,
                      bottom: 'auto' as any,
                      top: popupPosition?.top ?? 'auto',
                      left: popupPosition?.left ?? 20,
                      width: popupPosition?.width ?? 'calc(100% - 40px)',
                      maxWidth: 400,
                      marginBottom: 8,
                    } : null;

                    const nativeStyle = Platform.OS !== 'web' ? {
                      ...baseStyle,
                      position: 'absolute' as any,
                      bottom: 60,
                      left: 10,
                      right: 10,
                      marginBottom: 8,
                      zIndex: 9999,
                      elevation: 10,
                    } : null;

                    const popupContent = (
                      <View
                        style={(Platform.OS === 'web' ? webStyle : nativeStyle) as any}
                        onStartShouldSetResponder={() => true}
                        onMoveShouldSetResponder={() => true}
                      >
                        {loadingReferences ? (
                          <View style={{ padding: 16, alignItems: 'center' }}>
                            <Text style={{ color: theme ? '#666' : '#999' }}>Loading references...</Text>
                          </View>
                        ) : (
                          <>
                            <View style={{
                              padding: 12,
                              borderBottomWidth: 1,
                              borderBottomColor: theme ? '#e2e8f0' : '#334155',
                              backgroundColor: theme ? '#f8fafc' : '#0f172a'
                            }}>
                              <Text style={{
                                fontSize: 12,
                                fontWeight: '600',
                                color: theme ? '#666' : '#999',
                                textTransform: 'uppercase',
                                letterSpacing: 0.5
                              }}>
                                Reference {slashCommandQuery && `"${slashCommandQuery}"`}
                              </Text>
                            </View>
                            <ScrollView style={{ maxHeight: 250 }} nestedScrollEnabled>
                              {getFilteredReferences().length === 0 ? (
                                <View style={{ padding: 16, alignItems: 'center' }}>
                                  <Text style={{ color: theme ? '#666' : '#999' }}>
                                    {slashCommandQuery ? 'No results found' : 'Type to search...'}
                                  </Text>
                                </View>
                              ) : (
                                getFilteredReferences().map((item, index) => {
                                  const iconMap = {
                                    dataModel: 'database',
                                    dataItem: 'file-document',
                                    project: 'folder',
                                    calendar: 'calendar'
                                  };
                                  const colorMap = {
                                    dataModel: '#3b82f6',
                                    dataItem: '#10b981',
                                    project: '#f59e0b',
                                    calendar: '#ef4444'
                                  };

                                  return (
                                    <Pressable
                                      key={`${item.type}-${item.id}-${index}`}
                                      onPress={() => insertReference(item)}
                                      style={({ pressed }) => ({
                                        flexDirection: 'row',
                                        alignItems: 'center',
                                        padding: 12,
                                        backgroundColor: pressed
                                          ? (theme ? '#f1f5f9' : '#1e293b')
                                          : 'transparent',
                                        borderBottomWidth: index < getFilteredReferences().length - 1 ? 1 : 0,
                                        borderBottomColor: theme ? '#e2e8f0' : '#334155'
                                      })}
                                    >
                                      <View style={{
                                        width: 32,
                                        height: 32,
                                        borderRadius: 6,
                                        backgroundColor: `${colorMap[item.type]}20`,
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        marginRight: 12
                                      }}>
                                        <MaterialCommunityIcons
                                          name={iconMap[item.type] as any}
                                          size={18}
                                          color={colorMap[item.type]}
                                        />
                                      </View>
                                      <View style={{ flex: 1 }}>
                                        <Text style={{
                                          fontSize: 14,
                                          fontWeight: '600',
                                          color: theme ? '#000' : '#fff',
                                          marginBottom: 2
                                        }} numberOfLines={1}>
                                          {item.title}
                                        </Text>
                                        {item.subtitle && (
                                          <Text style={{
                                            fontSize: 12,
                                            color: theme ? '#666' : '#999'
                                          }} numberOfLines={1}>
                                            {item.subtitle}
                                          </Text>
                                        )}
                                      </View>
                                      <View style={{
                                        paddingHorizontal: 8,
                                        paddingVertical: 4,
                                        borderRadius: 4,
                                        backgroundColor: theme ? '#f1f5f9' : '#334155'
                                      }}>
                                        <Text style={{
                                          fontSize: 10,
                                          fontWeight: '600',
                                          color: colorMap[item.type],
                                          textTransform: 'uppercase'
                                        }}>
                                          {item.type === 'dataModel' ? 'Data' : item.type === 'dataItem' ? 'Item' : item.type}
                                        </Text>
                                      </View>
                                    </Pressable>
                                  );
                                })
                              )}
                            </ScrollView>
                          </>
                        )}
                      </View>
                    );

                    if (Platform.OS === 'web' && typeof document !== 'undefined') {
                      return createPortal(popupContent, document.body);
                    }
                    return (
                      <View style={{ position: 'relative' as any, zIndex: 9999, elevation: 10 }}>
                        {popupContent}
                      </View>
                    );
                  })()}

                  {(text?.trim()?.length > 0 || pendingImage?.base64) ? (
                    <Animatable.View useNativeDriver animation={'slideInUp'} style={{ left: forceColumn ? -30 : (messageInput ? -80 : -160), top: (staticPress || messageInput) ? undefined : Platform.select({ web: 35 }), flex: 1, maxWidth: 30 }}>
                      <Pressable
                        hitSlop={10}
                        android_ripple={{
                          color: COLORS.controlHighlight,
                          borderless: true,
                          radius: forceColumn ? 20 - 0.1 ^ 20 : 30 - 0.1 * 30,
                        }}
                        style={forceColumn ? styles.sendButtonSmall : styles.sendButton}
                        onPress={() => {
                          if (text?.trim()?.length > 0 || pendingImage.base64) {
                            if (onSend) {
                              onSend?.({
                                text: text,
                                image: pendingImage.uri || pendingImage.base64 || '',
                                createdAt: new Date(),
                                user: {
                                  _id: user._id,
                                  avatar: user.avatar,
                                },
                                to: targetUser?.id,
                              } as any);
                            } else {
                              sendMessageCallback?.(text);
                              Keyboard.dismiss();
                            }
                            setText('');
                          }
                        }}>
                        <Animated.View style={[opacityAnimatedStyle]}>
                          {
                            customSendIcon ?
                              (customSendIcon) :
                              <MaterialCommunityIcons
                                adjustsFontSizeToFit
                                allowFontScaling
                                name={'send'}
                                size={forceColumn ? 12 : 18}
                                color={COLORS.white}
                              />
                          }
                        </Animated.View>
                      </Pressable>
                    </Animatable.View>
                  ) : (<></>)}
                </Animated.View>
              </View>

            </Animated.View>

            {/* Metadata Dialog for Images/Videos */}
            <Dialog modal open={metadataDialogVisible} onOpenChange={setMetadataDialogVisible}>
              <Dialog.Portal>
                <Dialog.Overlay
                  animation="quick"
                  opacity={0.5}
                  enterStyle={{ opacity: 0 }}
                  exitStyle={{ opacity: 0 }}
                />
                <Dialog.Content
                  elevate
                  animation={[
                    'quick',
                    {
                      opacity: {
                        overshootClamping: true,
                      },
                    },
                  ]}
                  enterStyle={{ x: 0, y: -20, opacity: 0 }}
                  exitStyle={{ x: 0, y: 10, opacity: 0 }}
                  maxWidth={500}
                  width="90%"
                  backgroundColor={theme ? 'white' : '#1e293b'}
                >
                  <Dialog.Title style={{ color: theme ? '#000' : '#fff' }}>
                    Add Media Details
                  </Dialog.Title>
                  <Dialog.Description style={{ color: theme ? '#666' : '#999', marginBottom: 16 }}>
                    Add details to enable AI-powered social media post scheduling
                  </Dialog.Description>

                  <YStack gap="$3" paddingVertical="$2">
                    <YStack gap="$1">
                      <Text style={{ color: theme ? '#000' : '#fff', fontSize: 14, fontWeight: '500' }}>
                        Description
                      </Text>
                      <TextArea
                        value={mediaMetadata?.description || ''}
                        onChangeText={(text) => setMediaMetadata({ ...mediaMetadata, description: text })}
                        placeholder="Describe this media (for AI post generation)..."
                        placeholderTextColor={theme ? '#999' : '#666'}
                        backgroundColor={theme ? '#f8fafc' : '#334155'}
                        color={theme ? '#000' : '#fff'}
                        borderColor={theme ? '#e2e8f0' : '#475569'}
                        minHeight={80}
                      />
                    </YStack>

                    <YStack gap="$1">
                      <Text style={{ color: theme ? '#000' : '#fff', fontSize: 14, fontWeight: '500' }}>
                        Location (optional)
                      </Text>
                      <TextInput
                        value={mediaMetadata?.location || ''}
                        onChangeText={(text) => setMediaMetadata({ ...mediaMetadata, location: text })}
                        placeholder="e.g., New York, USA"
                        placeholderTextColor={theme ? '#999' : '#666'}
                        style={{
                          backgroundColor: theme ? '#f8fafc' : '#334155',
                          color: theme ? '#000' : '#fff',
                          borderWidth: 1,
                          borderColor: theme ? '#e2e8f0' : '#475569',
                          borderRadius: 8,
                          padding: 12,
                          fontSize: 14,
                        }}
                      />
                    </YStack>

                    <YStack gap="$1">
                      <Text style={{ color: theme ? '#000' : '#fff', fontSize: 14, fontWeight: '500' }}>
                        Schedule Post Date (optional)
                      </Text>
                      <TextInput
                        value={mediaMetadata?.scheduledDate || ''}
                        onChangeText={(text) => setMediaMetadata({ ...mediaMetadata, scheduledDate: text })}
                        placeholder="YYYY-MM-DD or leave empty for immediate"
                        placeholderTextColor={theme ? '#999' : '#666'}
                        style={{
                          backgroundColor: theme ? '#f8fafc' : '#334155',
                          color: theme ? '#000' : '#fff',
                          borderWidth: 1,
                          borderColor: theme ? '#e2e8f0' : '#475569',
                          borderRadius: 8,
                          padding: 12,
                          fontSize: 14,
                        }}
                      />
                    </YStack>

                    <YStack gap="$1">
                      <Text style={{ color: theme ? '#000' : '#fff', fontSize: 14, fontWeight: '500' }}>
                        Tags (comma-separated, optional)
                      </Text>
                      <TextInput
                        value={mediaMetadata?.tags || ''}
                        onChangeText={(text) => setMediaMetadata({ ...mediaMetadata, tags: text })}
                        placeholder="e.g., marketing, product, announcement"
                        placeholderTextColor={theme ? '#999' : '#666'}
                        style={{
                          backgroundColor: theme ? '#f8fafc' : '#334155',
                          color: theme ? '#000' : '#fff',
                          borderWidth: 1,
                          borderColor: theme ? '#e2e8f0' : '#475569',
                          borderRadius: 8,
                          padding: 12,
                          fontSize: 14,
                        }}
                      />
                    </YStack>
                  </YStack>

                  <XStack gap="$2" marginTop="$4" justifyContent="flex-end">
                    <Dialog.Close displayWhenAdapted asChild>
                      <Button
                        backgroundColor={theme ? '#f0f0f0' : '#475569'}
                        color={theme ? '#000' : '#fff'}
                        onPress={() => {
                          setMetadataDialogVisible(false);
                          setPendingMediaFile(null);
                          setMediaMetadata({
                            description: '',
                            location: '',
                            scheduledDate: '',
                            tags: ''
                          });
                        }}
                      >
                        Cancel
                      </Button>
                    </Dialog.Close>
                    <Button
                      backgroundColor={COLORS.accentLight}
                      color="white"
                      onPress={onMetadataSubmit}
                    >
                      Upload & Schedule
                    </Button>
                  </XStack>
                </Dialog.Content>
              </Dialog.Portal>
            </Dialog>

            {/* Product Selection Modal */}
            <ProductSelectionModal
              ref={productSelectionRef}
              onProductSelected={async (item, paymentProvider, paymentLink) => {
                // Payment link is already shortened by backend
                const itemData = item.data || {};
                const itemTitle = itemData.title || itemData.name || itemData.product_name || item.dataTitle || item.title || 'Product';
                const businessPhoneNumberId = (targetUser as any)?.businessPhoneNumberId;
                const recipientId = targetUser?.id;

                // Helper function to get product image
                const getProductImage = (item: any): string | null => {
                  // Check dataImages first
                  if (item.dataImages && item.dataImages.length > 0) {
                    return item.dataImages[0];
                  }

                  // Check images array
                  if (item.images && item.images.length > 0) {
                    return item.images[0];
                  }

                  // Check data object for image properties
                  const data = item.data || {};
                  const imageFields = ['image', 'images', 'thumbnail', 'picture', 'photo'];

                  for (const field of imageFields) {
                    if (data[field]) {
                      if (Array.isArray(data[field]) && data[field].length > 0) {
                        return data[field][0];
                      } else if (typeof data[field] === 'string') {
                        return data[field];
                      }
                    }
                  }

                  return null;
                };

                const productImage = getProductImage(item);
                const message = `🛒 Payment Link for ${itemTitle}\n\n${paymentLink}`;

                // If we're in WhatsApp context and have an image, send as media message
                if (businessPhoneNumberId && recipientId && productImage) {
                  try {
                    await sendMediaMessage({
                      to: recipientId,
                      mediaUrl: productImage,
                      mediaType: 'image',
                      caption: message,
                      businessPhoneNumberId: businessPhoneNumberId
                    });

                    // Also trigger onSend callback for UI update if available
                    if (onSend) {
                      onSend({
                        text: message,
                        image: productImage,
                        createdAt: new Date(),
                        user: {
                          _id: user._id,
                          avatar: user.avatar,
                        },
                        to: recipientId,
                      } as any);
                    }
                  } catch (error) {
                    console.error('Error sending media message, falling back to text:', error);
                    // Fallback to text message if media fails
                    if (onSend) {
                      onSend({
                        text: message,
                        createdAt: new Date(),
                        user: {
                          _id: user._id,
                          avatar: user.avatar,
                        },
                        to: recipientId,
                      } as any);
                    } else {
                      sendMessageCallback?.(message);
                    }
                  }
                } else {
                  // No image or not WhatsApp context - send as text
                  if (onSend) {
                    onSend({
                      text: message,
                      createdAt: new Date(),
                      user: {
                        _id: user._id,
                        avatar: user.avatar,
                      },
                      to: recipientId,
                    } as any);
                  } else {
                    sendMessageCallback?.(message);
                  }
                }
              }}
              recipientId={targetUser?.id}
              businessPhoneNumberId={(targetUser as any)?.businessPhoneNumberId}
            />
          </View>
        )
      }}
    </Dropzone>
  );
}

// Memoize MyInputToolbar to prevent unnecessary re-renders that cause focus loss
export default React.memo(MyInputToolbar, (prevProps, nextProps) => {
  // Only re-render if these critical props change
  // This prevents remounting when parent re-renders due to keyboard/layout changes
  return (
    prevProps.disableEntry === nextProps.disableEntry &&
    prevProps.onboardingInProgress === nextProps.onboardingInProgress &&
    prevProps.selectedMode === nextProps.selectedMode &&
    prevProps.externalText === nextProps.externalText &&
    prevProps.draggedOver === nextProps.draggedOver &&
    prevProps.metadataDialogVisible === nextProps.metadataDialogVisible &&
    prevProps.pendingMediaFile === nextProps.pendingMediaFile &&
    prevProps.isRecording === nextProps.isRecording &&
    prevProps.inputDisabled === nextProps.inputDisabled
  );
});

const styles = StyleSheet.create({
  container: {
    justifyContent: 'center',
    borderBottomStartRadius: 15,
    borderBottomEndRadius: 15,
  },
  replyContainer: {
    justifyContent: 'center',
    alignItems: 'flex-start',
    borderBottomStartRadius: 15,
    borderBottomEndRadius: 15,
  },
  title: {
    marginTop: 5,
    fontWeight: 'bold',
  },
  closeReply: {
    position: 'absolute',
    right: 10,
    top: 5,
  },
  reply: {
    marginTop: 5,
  },
  innerContainer: {
    justifyContent: 'space-between',
    alignItems: 'center',
    flexDirection: 'row',
    marginVertical: 10 - 0.1 * 10,
    minHeight: 60
  },
  inputAndMicrophone: {
    flexDirection: 'row',
    backgroundColor: COLORS.dimmed,
    flex: 3,
    marginRight: 10 - 0.1 * 10,
    paddingVertical: Platform.OS === 'ios' ? 10 : 0,
    borderRadius: 30 - 0.1 * 30,
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: 50
  },
  input: {
    backgroundColor: 'transparent',
    marginLeft: 12.5 - 0.1 * 12.5,
    color: COLORS.black,
    flex: 5,
    fontSize: fontValue(16),
    height: 55 - 0.1 * 55,
    alignSelf: 'stretch',
    borderRadius: 10,
    minWidth: "75%",
    padding: 5,
    borderWidth: 0,
    paddingVertical: 12,
    paddingRight: 30
  },
  rightIconButtonStyle: {
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 15 - 0.1 * 15,
    marginLeft: 5 - 0.1 * 5,
    borderLeftWidth: 0,
    borderLeftColor: COLORS.dimmed, // #fff
  },
  swipeToCancelView: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 30,
  },
  swipeText: {
    color: COLORS.darkGrey,
    fontSize: 15,
  },
  emoticonButton: {
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: 15 - 0.1 * 15,
  },
  recordingActive: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingLeft: 10,
  },
  recordingTime: {
    color: COLORS.darkGrey,
    fontSize: 20,
    marginLeft: 5,
  },
  microphoneAndLock: {
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  lockView: {
    backgroundColor: '#eee',
    width: 60,
    alignItems: 'center',
    borderTopLeftRadius: 30,
    borderTopRightRadius: 30,
    height: 130,
    paddingTop: 20,
  },
  sendButton: {
    borderRadius: 45 - 0.1 * 45,
    backgroundColor: COLORS.accentLight,
    height: 45 - 0.1 * 45,
    width: 45 - 0.1 * 45,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sendButtonSmall: {
    borderRadius: 23 - 0.1 * 23,
    backgroundColor: COLORS.accentLight,
    height: 23 - 0.1 * 23,
    width: 23 - 0.1 * 23,
    alignItems: 'center',
    justifyContent: 'center',
  },
});